import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  Textarea,
} from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { FormDescription } from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FileText, Plus, Trash2, Upload, X } from "lucide-react";

import ProductService from "@/services/productService";

import useExtendedWarrantyStore from "@/stores/useExtendedWarranty";

import {
  hasFilledBarems,
  hasFilledRateBarems,
  validateBarems,
} from "./validateBarems";

import CollectionTypesField, {
  DEFAULT_COLLECTION_TYPES,
} from "@/components/extendedWarranty/packages/collectionTypesField";
import CategorySelector from "@/components/shared/CategorySelector";
import ImageBlock from "@/components/shared/ImageBlock";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";
import { InputWithIcon } from "@/components/ui/inputWithIcon";

import * as z from "zod";

const NewPackagePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [planType, setPlanType] = useState("BASIC");
  const [merchantProducts, setMerchantProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [contractPdfFile, setContractPdfFile] = useState(null);
  const pdfInputRef = useRef(null);

  // Cascading category lists — fetched here so we can read each level's
  // imageUrl. CategorySelector already fetches the same lists internally; one
  // duplicate fetch is acceptable (responses are small and merchant-scoped).
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);

  // 3-level image state (mirrors product create page's pattern, see
  // project_3level_image_pattern memory note).
  const [mainCategoryImageUrl, setMainCategoryImageUrl] = useState("");
  const [categoryImageUrl, setCategoryImageUrl] = useState("");
  const [productImageUrl, setProductImageUrl] = useState("");
  const [categorySerialNoInfo, setCategorySerialNoInfo] = useState("");
  const [serialNoInfoBusy, setSerialNoInfoBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState({
    main: false,
    cat: false,
    product: false,
  });
  const mainImgRef = useRef(null);
  const catImgRef = useRef(null);
  const productImgRef = useRef(null);

  const planTypeTabs = [
    {
      id: "BASIC",
      title: t("extendedWarranties.planTypeBasic", "Standart Plan"),
    },
    {
      id: "BAREM",
      title: t("extendedWarranties.planTypeBarem", "Baremli Plan"),
    },
  ];

  // EPS-4856 (B-2/B-3): Zod default error messages İngilizce'ydi; Türkçe locale'de
  // bile "Number must be greater than or equal to 0" gözüküyordu. Her field için
  // explicit message + makul üst limit (10M TL fiyat alanları için, %100 oranlar için).
  const MAX_TL = 9_999_999; // 10M TL üst sınır — kullanıcı hatasını yakala
  const moneyField = () =>
    z
      .number()
      .min(0, { message: t("extendedWarranties.validateMsg.negativeAmount", "Fiyat negatif olamaz") })
      .max(MAX_TL, {
        message: t(
          "extendedWarranties.validateMsg.amountTooLarge",
          "Fiyat 9.999.999 TL'yi aşamaz",
        ),
      })
      .optional();
  const rateField = () =>
    z
      .number()
      .min(0, { message: t("extendedWarranties.validateMsg.negativeRate", "Oran negatif olamaz") })
      .max(100, {
        message: t("extendedWarranties.validateMsg.rateTooLarge", "Oran %100'den büyük olamaz"),
      })
      .optional();

  // All fields optional in schema; required-ness is enforced in onSubmit per planType
  // because BASIC fields are hidden when BAREM tab is active and vice versa.
  // EPS-4855: BAREM tab çoklu fiyat aralığı (barems[]) destekler.
  // Eski tek-satır alanlar (minPrice/maxPrice/planPrice/packageCode) geriye uyumluluk
  // için tutuluyor ama yeni FE her zaman barems array gönderiyor.
  const baremItemSchema = z.object({
    minPrice: moneyField(),
    maxPrice: moneyField(),
    planPrice: moneyField(),
    packageCode: z.string().optional().or(z.literal("")),
  });

  // EPS-4970: Standart Plan aralığı — baremli plandaki yapının aynısı, tek fark
  // aralığa düz fiyat yerine oran girilmesi. Paket fiyatı satış anında ürünün
  // kendi bedeli üzerinden (ürün fiyatı × oran) hesaplanır.
  const rateBaremItemSchema = z.object({
    minPrice: moneyField(),
    maxPrice: moneyField(),
    rate: rateField(),
    // Paket kodu aralık bazında ZORUNLU (plan seviyesinde kod yok) — boş satırlar
    // submit'te elendiği için şema permissive, kontrol validateBarems'te.
    packageCode: z.string().max(64).optional().or(z.literal("")),
  });

  const formSchema = z.object({
    name: z.string().optional().or(z.literal("")),
    // EPS-4863: ay bazlı süre. String olarak tutulur (input type=text);
    // payload'da parseInt ile sayıya çevrilir. 0 = bakım paketi, 0-72 aralığı.
    durationMonth: z
      .string()
      .min(
        1,
        t(
          "extendedWarranties.validateMsg.durationMonthRequired",
          "Süre (ay) zorunludur",
        ),
      )
      .regex(
        /^\d+$/,
        t(
          "extendedWarranties.validateMsg.durationMonthNatural",
          "Sadece doğal sayı girilebilir",
        ),
      )
      .refine(
        (v) => {
          const n = parseInt(v, 10);
          return n >= 0 && n <= 72;
        },
        t(
          "extendedWarranties.validateMsg.durationMonthRange",
          "Süre 0 ile 72 ay arasında olmalıdır",
        ),
      ),
    status: z.enum(["ACTIVE", "PASSIVE"]).optional(),
    // EPS-4930: baremli plan paket kategorisi — BAREM'de zorunlu, onSubmit'te
    // kontrol edilir (schema permissive; BASIC tab'da alan yok).
    packageCategory: z.enum(["EXTENDED_WARRANTY", "OTHER"]).optional(),
    // EPS-4970 revize: Plan Detayları (satış fiyatı, yüklenici/marka/bayi
    // komisyonları) formdan kaldırıldı — Standart Plan fiyatı artık yalnızca
    // oransal aralıktan hesaplanıyor, komisyon alanları hiçbir akışta
    // kullanılmıyordu. Alanlar BE'de duruyor (eski planların verisi ve admin
    // raporu için) ama panel artık kullanıcıdan istemiyor.
    mainCategoryId: z.string().optional().or(z.literal("")),
    categoryId: z.string().optional().or(z.literal("")),
    subCategoryId: z.string().optional().or(z.literal("")),
    subCategoryBrandId: z.string().optional().or(z.literal("")),
    merchantProductId: z.string().optional().or(z.literal("")),
    barems: z.array(baremItemSchema).optional(),
    // EPS-4970: Standart Planın oransal barem aralıkları.
    rateBarems: z.array(rateBaremItemSchema).optional(),
    // EPS-4971: Tahsilat yöntemi — en az bir yöntem zorunlu. Plan tipinden
    // bağımsız; hem Standart hem Baremli planda aynı alan.
    collectionTypes: z
      .array(z.enum(["SELLER", "ONLINE"]))
      .min(
        1,
        t(
          "extendedWarranties.validateMsg.collectionTypeRequired",
          "En az bir tahsilat yöntemi seçilmelidir",
        ),
      ),
    // EPS-5860: panel'den girilen plan açıklaması — embed'de AddonCard'da gösterilir
    // (Murat Bey BE PR #205 + product-api #446). Boş gönderilirse BE varsayılan
    // metni ekler, FE'de zorunlu değil.
    planDescription: z.string().max(2000).optional().or(z.literal("")),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      durationMonth: "12",
      status: "ACTIVE",
      packageCategory: "EXTENDED_WARRANTY",
      mainCategoryId: "",
      categoryId: "",
      subCategoryId: "",
      subCategoryBrandId: "",
      merchantProductId: "",
      barems: [
        {
          minPrice: undefined,
          maxPrice: undefined,
          planPrice: undefined,
          packageCode: "",
        },
      ],
      // EPS-4970 revize: Standart Planın tek fiyat kaynağı oransal aralıklar
      // olduğu için baremli plandaki gibi bir boş satırla başlıyor.
      rateBarems: [
        {
          minPrice: undefined,
          maxPrice: undefined,
          rate: undefined,
          packageCode: "",
        },
      ],
      planDescription: "",
      // EPS-4971: Bayi tahsilatı seçili gelir — bugüne kadarki tek davranış.
      collectionTypes: DEFAULT_COLLECTION_TYPES,
    },
  });

  // EPS-4855: çoklu barem yönetimi — kullanıcı + butonuna basarak yeni satır
  // ekleyebilir, çöp ikonuyla silebilir. En az 1 satır zorunlu (DB'ye boş gitmesin).
  const baremsArray = useFieldArray({
    control: form.control,
    name: "barems",
  });

  // EPS-4970: Standart Planın oransal aralıkları. Revizeden sonra planın TEK
  // fiyat kaynağı bu aralıklar — baremli plandaki gibi en az 1 satır zorunlu.
  const rateBaremsArray = useFieldArray({
    control: form.control,
    name: "rateBarems",
  });

  const watchedCategoryId = form.watch("categoryId");
  const watchedMainCategoryId = form.watch("mainCategoryId");
  const watchedSubCategoryId = form.watch("subCategoryId");
  const watchedSubCategoryBrandId = form.watch("subCategoryBrandId");
  const watchedProductId = form.watch("merchantProductId");

  useEffect(() => {
    if (!watchedCategoryId) {
      setMerchantProducts([]);
      return;
    }
    let cancelled = false;
    setLoadingProducts(true);
    ProductService.getProducts({
      categoryId: watchedCategoryId,
      mainCategoryId: watchedMainCategoryId,
      subCategoryId: watchedSubCategoryId || undefined,
      subCategoryBrandId: watchedSubCategoryBrandId || undefined,
      page: 1,
      size: 200,
    })
      .then((res) => {
        if (!cancelled) {
          setMerchantProducts(res?.content || []);
        }
      })
      .catch(() => {
        if (!cancelled) setMerchantProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [planType, watchedCategoryId, watchedMainCategoryId, watchedSubCategoryId, watchedSubCategoryBrandId]);

  // Layer 1: main categories. EPS-4970: kategori seçimi iki plan tipinde de var,
  // sekmeye bağlı guard kaldırıldı.
  // EPS-4856 (B-14): id bazlı dedup; BE response'da duplicate kategori dönüyorsa
  // image lookup yanlış imageUrl getirebilir.
  // EPS-4856 (B-15 partial): CategorySelector da ayrı bir fetch atıyor; tam dedup
  // için CategorySelector'a onMainCategoriesLoaded callback eklenmeli (gelecek
  // PR), şu an her iki tarafta da dedup uygulanıyor.
  useEffect(() => {
    let cancelled = false;
    ProductService.getMainCategories()
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data || res?.data || res || [];
        const safe = Array.isArray(list) ? list : [];
        const deduped = Array.from(new Map(safe.map((c) => [c.id, c])).values());
        setMainCategories(deduped);
      })
      .catch(() => {
        if (!cancelled) setMainCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planType]);

  // Layer 2: categories — refetch on main change.
  useEffect(() => {
    if (!watchedMainCategoryId) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    ProductService.getCategories(watchedMainCategoryId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planType, watchedMainCategoryId]);

  // Resolve each level's imageUrl from its list whenever id or list changes.
  useEffect(() => {
    if (!watchedMainCategoryId) {
      setMainCategoryImageUrl("");
      return;
    }
    const m = mainCategories.find(
      (c) => String(c.id) === String(watchedMainCategoryId),
    );
    setMainCategoryImageUrl(m?.imageUrl || "");
  }, [watchedMainCategoryId, mainCategories]);

  useEffect(() => {
    if (!watchedCategoryId) {
      setCategoryImageUrl("");
      setCategorySerialNoInfo("");
      return;
    }
    const c = categories.find(
      (x) => String(x.id) === String(watchedCategoryId),
    );
    setCategoryImageUrl(c?.imageUrl || "");
    setCategorySerialNoInfo(c?.serialNoInfo || "");
  }, [watchedCategoryId, categories]);

  useEffect(() => {
    if (!watchedProductId) {
      setProductImageUrl("");
      return;
    }
    const p = merchantProducts.find(
      (x) => String(x.id) === String(watchedProductId),
    );
    setProductImageUrl(p?.imageUrl || "");
  }, [watchedProductId, merchantProducts]);

  // Image upload — generic handler keyed by level.
  const handleImageUpload = async (level, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const MAX = 40 * 1024 * 1024;
    if (file.size > MAX) {
      toast({
        variant: "error",
        title: t("productsPage.fileSizeError", "Dosya boyutu çok büyük"),
        description: t(
          "productsPage.fileSizeErrorDesc",
          "Dosya boyutu maksimum 40 MB olmalıdır.",
        ),
      });
      return;
    }

    setImageBusy((b) => ({ ...b, [level]: true }));
    try {
      if (level === "main" && watchedMainCategoryId) {
        const uploadRes = await ProductService.uploadFile(file);
        const url = uploadRes?.url || uploadRes?.data?.url;
        if (!url) throw new Error("upload-url-missing");
        await ProductService.updateMainCategoryImage(watchedMainCategoryId, url);
        setMainCategoryImageUrl(url);
      } else if (level === "cat" && watchedCategoryId) {
        const uploadRes = await ProductService.uploadFile(file);
        const url = uploadRes?.url || uploadRes?.data?.url;
        if (!url) throw new Error("upload-url-missing");
        await ProductService.updateCategoryImage(watchedCategoryId, url);
        setCategoryImageUrl(url);
      } else if (level === "product" && watchedProductId) {
        // Product image upload uses multipart — the BE endpoint handles
        // both file-upload and image-record write in one request.
        const formData = new FormData();
        formData.append("file", file);
        const res = await ProductService.postProductImage(
          watchedProductId,
          formData,
        );
        const url = res?.data?.imageUrl || res?.imageUrl || "";
        setProductImageUrl(url);
      }
      toast({
        title: t("productsPage.imageUploadSuccess", "Görsel güncellendi"),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.imageUploadError", "Görsel yüklenemedi"),
      });
    } finally {
      setImageBusy((b) => ({ ...b, [level]: false }));
    }
  };

  const handleImageDelete = async (level) => {
    setImageBusy((b) => ({ ...b, [level]: true }));
    try {
      if (level === "main" && watchedMainCategoryId) {
        await ProductService.deleteMainCategoryImage(watchedMainCategoryId);
        setMainCategoryImageUrl("");
      } else if (level === "cat" && watchedCategoryId) {
        await ProductService.deleteCategoryImage(watchedCategoryId);
        setCategoryImageUrl("");
      } else if (level === "product" && watchedProductId) {
        await ProductService.deleteProductImage(watchedProductId);
        setProductImageUrl("");
      }
      toast({
        title: t("productsPage.imageDeleteSuccess", "Görsel silindi"),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.imageDeleteError", "Görsel silinemedi"),
      });
    } finally {
      setImageBusy((b) => ({ ...b, [level]: false }));
    }
  };

  const handleSerialNoInfoSave = async () => {
    if (!watchedCategoryId) return;
    setSerialNoInfoBusy(true);
    try {
      await ProductService.updateCategorySerialNoInfo(
        watchedCategoryId,
        categorySerialNoInfo.trim() || null,
      );
      toast({
        title: t(
          "extendedWarranties.serialNoInfoSaved",
          "Seri no bilgisi güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "extendedWarranties.serialNoInfoSaveError",
            "Seri no bilgisi güncellenemedi",
          ),
      });
    } finally {
      setSerialNoInfoBusy(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  const handlePdfSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const MAX_PDF = 10 * 1024 * 1024;
    if (file.size > MAX_PDF) {
      toast({
        variant: "error",
        title: t(
          "extendedWarranties.pdfSizeError",
          "PDF dosyası en fazla 10 MB olabilir",
        ),
      });
      return;
    }
    setContractPdfFile(file);
  };

  const onSubmit = async (data) => {
    // planType-aware required field check (schema is intentionally permissive)
    if (planType === "BASIC") {
      // EPS-4856 (B-1 + B-19): field-level error + trim + min 2 karakter
      if (!data.name || data.name.trim().length < 2) {
        const msg = t(
          "extendedWarranties.validateMsg.nameTooShort",
          "Plan adı en az 2 karakter olmalıdır",
        );
        form.setError("name", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // EPS-4970: Paket kategorisi zorunlu (baremli plandaki ile aynı kural).
      if (!data.packageCategory) {
        const msg = t(
          "extendedWarranties.validateMsg.packageCategoryRequired",
          "Paket kategorisi zorunludur",
        );
        form.setError("packageCategory", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // EPS-4970: Plan bir kategoriye bağlanmalı — kategori olmadan hangi
      // ürünlere uygulanacağı belirsiz kalır (baremli plandaki ana kategori
      // zorunluluğunun aynısı).
      if (!data.mainCategoryId) {
        const msg = t(
          "extendedWarranties.validateMsg.mainCategoryRequired",
          "Ana kategori zorunludur",
        );
        form.setError("mainCategoryId", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // Oransal barem aralıkları — baremli plandaki kurallarla birebir,
      // ek olarak paket kodu zorunlu (plan seviyesinde kod yok).
      const rateBaremErrors = validateBarems(data.rateBarems, t, {
        priceField: "rate",
        fieldPrefix: "rateBarems",
        requirePackageCode: true,
      });
      if (rateBaremErrors.length > 0) {
        rateBaremErrors.forEach((e) =>
          form.setError(e.path, { type: "manual", message: e.message }),
        );
        toast({ title: rateBaremErrors[0].message, variant: "error" });
        return;
      }
      // EPS-4970 revize: Plan Detayları kaldırıldıktan sonra planın tek fiyat
      // kaynağı oransal aralıklar — baremli plandaki gibi en az 1 aralık şart,
      // yoksa plan hiçbir kanalda fiyatlandırılamaz.
      if (!hasFilledRateBarems(data.rateBarems)) {
        const msg = t(
          "extendedWarranties.validateMsg.atLeastOneRateBarem",
          "En az bir fiyat aralığı girmelisiniz",
        );
        form.setError("rateBarems.0.minPrice", {
          type: "manual",
          message: msg,
        });
        toast({ title: msg, variant: "error" });
        return;
      }
    } else if (planType === "BAREM") {
      // EPS-4856 (B-1 + B-19): field-level error + trim + min 2 karakter
      if (!data.name || data.name.trim().length < 2) {
        const msg = t(
          "extendedWarranties.validateMsg.nameTooShort",
          "Plan adı en az 2 karakter olmalıdır",
        );
        form.setError("name", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // EPS-4930: Paket kategorisi zorunlu (Uzatılmış Garanti / Diğer).
      if (!data.packageCategory) {
        const msg = t(
          "extendedWarranties.validateMsg.packageCategoryRequired",
          "Paket kategorisi zorunludur",
        );
        form.setError("packageCategory", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // EPS-4856 (B-3): Baremli planda ana kategori zorunlu (mantıklı default).
      // Kategori olmadan plan barem kuralları hangi ürünlere uygulanacak belirsiz.
      if (!data.mainCategoryId) {
        const msg = t(
          "extendedWarranties.validateMsg.mainCategoryRequired",
          "Ana kategori zorunludur",
        );
        form.setError("mainCategoryId", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // Kategori opsiyonel: hiç seçim yok = tüm kategoriler, sadece main = o ana
      // kategori, main+sub = o alt kategori, +product = sadece o ürün.
      // Package Code da opsiyonel (BE null kabul eder).
      const baremErrors = validateBarems(data.barems, t);
      if (baremErrors.length > 0) {
        baremErrors.forEach((e) =>
          form.setError(e.path, { type: "manual", message: e.message }),
        );
        toast({ title: baremErrors[0].message, variant: "error" });
        return;
      }
      // Baremli planın tek fiyat kaynağı bu aralıklar. validateBarems tamamen
      // boş satırları atladığı için hiç aralık girilmeden submit geçebiliyordu
      // ve plan boş barems[] ile kaydediliyordu — Standart plandaki
      // hasFilledRateBarems kontrolünün baremli plandaki karşılığı.
      if (!hasFilledBarems(data.barems)) {
        const msg = t(
          "extendedWarranties.validateMsg.atLeastOneBarem",
          "En az bir fiyat aralığı girmelisiniz",
        );
        form.setError("barems.0.minPrice", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
    }

    try {
      const contractPdf = contractPdfFile
        ? {
            name: contractPdfFile.name,
            mimeType: contractPdfFile.type,
            content: await fileToBase64(contractPdfFile),
          }
        : null;

      // EPS-4855: BAREM çoklu barem desteği — barems array gönder.
      // BE PR #201: planBarem.barems: [{minPrice, maxPrice, planPrice, packageCode}, ...]
      const baremsPayload = (data.barems || [])
        .map((b) => ({
          minPrice: b.minPrice,
          maxPrice: b.maxPrice,
          planPrice: b.planPrice,
          packageCode: b.packageCode || null,
        }))
        // En az 1 alan dolu olan satırlar BE'ye gider; tamamen boş satırlar atlanır.
        .filter(
          (b) =>
            b.minPrice !== undefined ||
            b.maxPrice !== undefined ||
            b.planPrice !== undefined ||
            (b.packageCode && b.packageCode.length > 0),
        );

      // EPS-4970: Standart Planın oransal aralıkları. Tamamen boş satırlar atlanır.
      const rateBaremsPayload = (data.rateBarems || [])
        .map((b) => ({
          minPrice: b.minPrice,
          maxPrice: b.maxPrice,
          rate: b.rate,
          packageCode: b.packageCode || null,
        }))
        .filter(
          (b) =>
            b.minPrice !== undefined ||
            b.maxPrice !== undefined ||
            b.rate !== undefined ||
            (b.packageCode && b.packageCode.length > 0),
        );

      // EPS-5860: planDescription — boşsa BE varsayılan metni ekler.
      const planDescriptionPayload = (data.planDescription || "").trim() || null;

      // EPS-4863: durationMonth otorite alandır. warrantyYear ve planDuration
      // eski tüketiciler için ay sayısından türetilir (BE de durationMonth
      // gönderilmezse warrantyYear*12 ile türetir; biz her zaman gönderiyoruz).
      // String'ten parseInt: zod regex zaten doğal sayıyı garantilemiş durumda.
      const durationMonth = parseInt(data.durationMonth, 10);
      const derivedWarrantyYear = Math.round(durationMonth / 12);
      const derivedPlanDuration =
        durationMonth >= 36
          ? "THREE_YEAR"
          : durationMonth >= 24
            ? "TWO_YEAR"
            : "ONE_YEAR";

      const transformedData =
        planType === "BAREM"
          ? {
              planType: "BAREM",
              planBarem: {
                name: data.name,
                categoryId: data.categoryId ? Number(data.categoryId) : null,
                mainCategoryId: data.mainCategoryId
                  ? Number(data.mainCategoryId)
                  : null,
                subCategoryId: data.subCategoryId
                  ? Number(data.subCategoryId)
                  : null,
                subCategoryBrandId: data.subCategoryBrandId
                  ? Number(data.subCategoryBrandId)
                  : null,
                merchantProductId: data.merchantProductId
                  ? Number(data.merchantProductId)
                  : null,
                durationMonth,
                warrantyYear: derivedWarrantyYear,
                status: data.status,
                packageCategory: data.packageCategory,
                barems: baremsPayload,
                planDescription: planDescriptionPayload,
              },
              contractPdf,
              // EPS-4971: tahsilat yöntemi plan tipinden bağımsız → istek kökünde.
              collectionTypes: data.collectionTypes,
            }
          : {
              planType: "BASIC",
              planBasics: {
                name: data.name,
                planType: t("extendedWarranties.extendedWarrantyPlan"),
                durationMonth,
                planDuration: derivedPlanDuration,
                status: data.status,
                planDescription: planDescriptionPayload,
                // EPS-4970: Standart Plan da baremli plan gibi kategori hiyerarşisine
                // bağlanıyor ve paket kategorisi taşıyor.
                packageCategory: data.packageCategory,
                mainCategoryId: data.mainCategoryId ? Number(data.mainCategoryId) : null,
                categoryId: data.categoryId ? Number(data.categoryId) : null,
                subCategoryId: data.subCategoryId ? Number(data.subCategoryId) : null,
                subCategoryBrandId: data.subCategoryBrandId
                  ? Number(data.subCategoryBrandId)
                  : null,
                merchantProductId: data.merchantProductId
                  ? Number(data.merchantProductId)
                  : null,
                // Oransal barem aralıkları. Her zaman array gönderiyoruz;
                // BE'de null = "dokunma", [] = "tümünü temizle" semantiği var.
                barems: rateBaremsPayload,
              },
              // EPS-4970 revize: planDetails (satış fiyatı + komisyon oranları)
              // artık gönderilmiyor — kullanıcıdan istenmiyor ve hiçbir
              // fiyatlandırma akışında kullanılmıyordu. BE alanı null geldiğinde
              // tolere ediyor (MerchantAdditionalServicePlanConverter).
              planCollectionAndGivingBack: {
                givingBackPolicy: t("extendedWarranties.fourteenDays"),
                isActivationCode: true,
                activationCodeCount: 100,
              },
              contractPdf,
              // EPS-4971: tahsilat yöntemi plan tipinden bağımsız → istek kökünde.
              // planCollectionAndGivingBack.collectionType artık gönderilmiyor;
              // BE onu yalnızca eski panel sürümleri için geriye uyum olarak okuyor.
              collectionTypes: data.collectionTypes,
            };

      await useExtendedWarrantyStore
        .getState()
        .createAdditionalServicePlan(transformedData);
      // EPS-4856 (B-9): başarı toast'u + (B-6) form state reset.
      toast({
        title: t(
          "extendedWarranties.planCreatedSuccess",
          "Plan başarıyla oluşturuldu",
        ),
        variant: "success",
      });
      form.reset();
      navigate("/extended-warranties/packages");
    } catch (error) {
      // EPS-4856 (B-4): BE i18n key dönüyorsa (örn merchant.plan.name.already.exist)
      // Locize'dan çevir; key Locize'da yoksa raw mesajı fallback olarak göster.
      const raw = error.response?.data?.errors?.[0]?.message;
      const translated = raw ? t(raw, raw) : null;
      // EPS-4856 (B-7): duplicate name hatası → Plan Name field-level binding
      // (toast yeterli değil; kullanıcı hangi field'ın hatalı olduğunu görmeli).
      if (raw && /already.?exist/i.test(raw)) {
        form.setError("name", {
          type: "manual",
          message: translated || raw,
        });
      }
      toast({
        title:
          translated ||
          t("common.unexpectedError", "Beklenmeyen bir hata oluştu."),
        variant: "error",
      });
    }
  };

  // BUG-3 fix: schema validation reddi sessiz kalmasın; ilk hatayı toast et.
  const onSubmitError = (errors) => {
    const firstField = Object.keys(errors || {})[0];
    const firstMsg =
      firstField &&
      (errors[firstField]?.message ||
        t(
          "extendedWarranties.validateMsg.formInvalid",
          "Lütfen formu kontrol edin",
        ));
    toast({
      title:
        firstMsg ||
        t(
          "extendedWarranties.validateMsg.formInvalid",
          "Lütfen formu kontrol edin",
        ),
      variant: "error",
    });
  };

  // EPS-4970: Kategori/ürün seçimi artık HER İKİ plan tipinde de var — Standart
  // Plan da baremli plan gibi kategori hiyerarşisine bağlanıyor. Bölüm tek yerde
  // tanımlanıp iki sekmede de kullanılıyor; aynı anda yalnızca biri mount olduğu
  // için (aşağıda BASIC bloğu da koşullu render ediliyor) çift fetch/duplike id
  // riski yok.
  const categorySection = (
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <h2 className="mb-6 text-lg font-semibold">
                  {t(
                    "extendedWarrantiesPage.baremCategoryInfo",
                    "Kategori Bilgileri",
                  )}
                </h2>
                {/* EPS-4873: 4 seviyeli kategori hiyerarşisi (ana kategori
                   > kategori > alt kategori > marka). Plan bu seviyelerden
                   en az birine bağlanır; resolve sırasında en spesifik
                   eşleşme öncelik kazanır. */}
                <CategorySelector
                  value={{
                    mainCategoryId: form.watch("mainCategoryId") || "",
                    categoryId: form.watch("categoryId") || "",
                    subCategoryId: form.watch("subCategoryId") || "",
                    subCategoryBrandId: form.watch("subCategoryBrandId") || "",
                  }}
                  onChange={(val) => {
                    form.setValue("mainCategoryId", val.mainCategoryId || "");
                    form.setValue("categoryId", val.categoryId || "");
                    form.setValue("subCategoryId", val.subCategoryId || "");
                    form.setValue(
                      "subCategoryBrandId",
                      val.subCategoryBrandId || "",
                    );
                    form.setValue("merchantProductId", "");
                  }}
                  showMainCategory
                  showCategory
                  showSubCategory
                  showBrand
                  showAllOption={false}
                />

                {/* Layer 1 — Kategori (mainCategoryId) görseli */}
                {watchedMainCategoryId ? (
                  <div className="mt-4 flex flex-col gap-2">
                    <Label>
                      {t(
                        "extendedWarranties.mainCategoryImage",
                        "Kategori Görseli",
                      )}
                    </Label>
                    <ImageBlock
                      t={t}
                      levelLabel={t(
                        "productsPage.mainCategory",
                        "Kategori",
                      )}
                      url={mainCategoryImageUrl}
                      busyFlag={imageBusy.main}
                      inputRef={mainImgRef}
                      onUpload={(e) => handleImageUpload("main", e)}
                      onDelete={() => handleImageDelete("main")}
                      hint={t(
                        "productsPage.categoryImageHint",
                        "Bu görsel seçilen kategoride paylaşılır.",
                      )}
                    />
                  </div>
                ) : null}

                {/* Layer 2 — Alt Kategori (categoryId) görseli */}
                {watchedCategoryId ? (
                  <div className="mt-4 flex flex-col gap-2">
                    <Label>
                      {t(
                        "productsPage.subCategoryImage",
                        "Alt Kategori Görseli",
                      )}
                    </Label>
                    <ImageBlock
                      t={t}
                      levelLabel={t(
                        "productsPage.subCategory",
                        "Alt Kategori",
                      )}
                      url={categoryImageUrl}
                      busyFlag={imageBusy.cat}
                      inputRef={catImgRef}
                      onUpload={(e) => handleImageUpload("cat", e)}
                      onDelete={() => handleImageDelete("cat")}
                      hint={t(
                        "productsPage.subCategoryImageHint",
                        "Bu görsel seçilen alt kategoride paylaşılır.",
                      )}
                    />
                  </div>
                ) : null}

                {/* EPS-4850: Kategori seri no bilgisi — embed-app'te seri no
                   input yanındaki "i" ikonunun tooltip metni. Kategori bazlı
                   bir ayar olduğundan buradan girip kaydedilir. */}
                {watchedCategoryId ? (
                  <div className="mt-4 flex flex-col gap-2">
                    <Label>
                      {t(
                        "extendedWarranties.categorySerialNoInfo",
                        "Seri No Bilgisi",
                      )}
                    </Label>
                    <Textarea
                      rows={3}
                      maxLength={500}
                      placeholder={t(
                        "extendedWarranties.categorySerialNoInfoPlaceholder",
                        "Müşterilere seri numarasını nerede bulacaklarını açıklayın (örn: ürünün arka etiketinde 15 haneli numara)",
                      )}
                      value={categorySerialNoInfo}
                      onChange={(e) => setCategorySerialNoInfo(e.target.value)}
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {t(
                          "extendedWarranties.categorySerialNoInfoHint",
                          "embed.egaranti.com'da seri no alanının yanındaki \"i\" ikonunda gösterilir.",
                        )}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={serialNoInfoBusy}
                        onClick={handleSerialNoInfoSave}
                      >
                        {serialNoInfoBusy
                          ? t("common.saving", "Kaydediliyor...")
                          : t("common.save", "Kaydet")}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {watchedCategoryId && (
                  <div className="mt-4 flex flex-col gap-2">
                    <Label>
                      {t(
                        "extendedWarranties.merchantProductOptional",
                        "Ürün (Opsiyonel)",
                      )}
                    </Label>
                    {/*
                      key bound to list length: Radix Select otherwise caches
                      a "no match" verdict if the user-provided value arrives
                      before the items list, and never re-evaluates after the
                      async fetch resolves. Forces remount when the list
                      materialises so the controlled value is re-applied.
                      See B29a in project_bug_class_catalog memory note.
                    */}
                    <Select
                      key={`product-${merchantProducts.length}`}
                      value={form.watch("merchantProductId") || "__ALL__"}
                      onValueChange={(value) =>
                        form.setValue(
                          "merchantProductId",
                          value === "__ALL__" ? "" : value,
                        )
                      }
                      disabled={loadingProducts}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "extendedWarranties.selectProductOrAll",
                            "Tüm kategori için (varsayılan)",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__ALL__">
                          {t(
                            "extendedWarranties.allCategoryProducts",
                            "Tüm kategori için",
                          )}
                        </SelectItem>
                        {merchantProducts.map((p) => (
                          <SelectItem key={p.id} value={p.id.toString()}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* EPS-4970: Kategori bölümü artık iki plan tipinde de kullanılıyor;
                       metin plan tipine göre değişmeli — yoksa Standart Plan sekmesinde
                       "BAREM" ifadesi görünüyordu. */}
                    <FormDescription>
                      {planType === "BAREM"
                        ? t(
                            "extendedWarranties.merchantProductHint",
                            "Boş bırakırsanız BAREM tüm bu kategori için geçerli olur. Spesifik bir ürün seçerseniz BAREM sadece o ürüne uygulanır.",
                          )
                        : t(
                            "extendedWarranties.merchantProductHintBasic",
                            "Boş bırakırsanız Standart Plan tüm bu kategori için geçerli olur. Spesifik bir ürün seçerseniz plan sadece o ürüne uygulanır.",
                          )}
                    </FormDescription>

                    {/* Layer 3 — Ürün görseli (only when a specific product is picked) */}
                    {watchedProductId ? (
                      <div className="mt-2 flex flex-col gap-2">
                        <Label>
                          {t("productsPage.productImage", "Ürün Görseli")}
                        </Label>
                        <ImageBlock
                          t={t}
                          levelLabel={t(
                            "productsPage.productImage",
                            "Ürün Görseli",
                          )}
                          url={productImageUrl}
                          busyFlag={imageBusy.product}
                          inputRef={productImgRef}
                          onUpload={(e) => handleImageUpload("product", e)}
                          onDelete={() => handleImageDelete("product")}
                          hint={t(
                            "extendedWarranties.productImageHint",
                            "Bu görsel seçilen ürün için kaydedilir.",
                          )}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
  );

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/extended-warranties/packages">
          {t("navbar.menuItems.packages")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("extendedWarrantiesPage.newPackage")}
        </BreadcrumbItem>
      </Breadcrumb>
      <div className="container mx-auto px-4 py-8">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onSubmitError)}
            className="space-y-6"
          >
            {/* EPS-4970: Plan bilgileri ve kategori artık her iki plan tipinde de
               aynı — ortak bölüm olarak en üstte duruyor. Plan tipi seçimi,
               yapının gerçekten ayrıştığı yere (fiyatlandırma) taşındı. */}
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-lg font-semibold">
                {t(
                  "extendedWarrantiesPage.packageBasicInfo",
                  "Plan Bilgileri",
                )}
              </h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("extendedWarranties.name")}<span className="ml-0.5 text-red-500">*</span></Label>
                      <FormControl>
                        <Input maxLength={50} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="durationMonth"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("extendedWarranties.durationMonth", "Süre (Ay)")}<span className="ml-0.5 text-red-500">*</span></Label>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* EPS-4930: Paket kategorisi — Uzatılmış Garanti / Diğer.
                   BE'de package_category kolonuna yazılır. */}
                <FormField
                  control={form.control}
                  name="packageCategory"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <Label>
                        {t(
                          "extendedWarranties.packageCategory",
                          "Paket Kategorisi",
                        )}
                        <span className="ml-0.5 text-red-500">*</span>
                      </Label>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                        className="flex items-center gap-2"
                      >
                        <FormItem className="flex items-center space-x-3">
                          <FormControl>
                            <RadioGroupItem
                              value="EXTENDED_WARRANTY"
                              id="plan-package-category-ew"
                            />
                          </FormControl>
                          <Label
                            htmlFor="plan-package-category-ew"
                            className="!mt-0 cursor-pointer"
                          >
                            {t(
                              "extendedWarranties.packageCategoryExtendedWarranty",
                              "Uzatılmış Garanti",
                            )}
                          </Label>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3">
                          <FormControl>
                            <RadioGroupItem
                              value="OTHER"
                              id="plan-package-category-other"
                            />
                          </FormControl>
                          <Label
                            htmlFor="plan-package-category-other"
                            className="!mt-0 cursor-pointer"
                          >
                            {t(
                              "extendedWarranties.packageCategoryOther",
                              "Diğer",
                            )}
                          </Label>
                        </FormItem>
                      </RadioGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <Label>{t("extendedWarranties.status")}<span className="ml-0.5 text-red-500">*</span></Label>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex items-center gap-2"
                      >
                        <FormItem className="flex items-center space-x-3">
                          <FormControl>
                            <RadioGroupItem
                              value="ACTIVE"
                              id="plan-active-status"
                            />
                          </FormControl>
                          <Label
                            htmlFor="plan-active-status"
                            className="!mt-0 cursor-pointer"
                          >
                            {t("extendedWarranties.active")}
                          </Label>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3">
                          <FormControl>
                            <RadioGroupItem
                              value="PASSIVE"
                              id="plan-passive-status"
                            />
                          </FormControl>
                          <Label
                            htmlFor="plan-passive-status"
                            className="!mt-0 cursor-pointer"
                          >
                            {t("extendedWarranties.passive")}
                          </Label>
                        </FormItem>
                      </RadioGroup>
                    </FormItem>
                  )}
                />

                {/* EPS-4971: Tahsilat yöntemi — satışta hangi aksiyonun
                   görüneceğini belirler (bayi tahsilatı / ödeme linki). */}
                <CollectionTypesField control={form.control} />
              </div>

              {/* EPS-5860: Plan açıklaması — embed-app AddonCard'da gösterilir.
                 Boş bırakılırsa BE varsayılan metni ekler. */}
              <div className="mt-6">
                <FormField
                  control={form.control}
                  name="planDescription"
                  render={({ field }) => (
                    <FormItem>
                      <Label>
                        {t(
                          "extendedWarranties.planDescription",
                          "Plan Açıklaması",
                        )}
                      </Label>
                      <FormControl>
                        <Textarea
                          rows={4}
                          maxLength={2000}
                          placeholder={t(
                            "extendedWarranties.planDescriptionPlaceholder",
                            "Bu paket ile son tüketicinin elde edeceği avantajları açıklayın (kapsam, onarım, değişim, muafiyet vb.)",
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t(
                          "extendedWarranties.planDescriptionHint",
                          "embed.egaranti.com'da paket kartı altında müşteriye gösterilir. Boş bırakırsanız standart bir metin eklenir.",
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {categorySection}

            {/* EPS-4970: Buradan sonrası plan tipine göre ayrışıyor. Standart planda
               plan detayları + oransal aralıklar, baremli planda düz fiyatlı
               aralıklar. Üstteki ortak alanlar sekme değişiminde korunuyor. */}
            {/* mb-6 kaldırıldı: sekme artık form'un içinde ve space-y-6 aralığı veriyor */}
            {/* Tabs'ın sekme butonlarında type="button" yok. EPS-4970 ile sekme
               form'un içine taşınınca sekmeye tıklamak implicit submit tetikliyor
               ve plan, barem aralıkları girilmeden kaydediliyordu. onTabChange
               butonun kendi handler'ında (bubble'dan önce) çalıştığı için
               default'u burada iptal etmek sekme değişimini bozmadan submit'i
               engelliyor. */}
            <div
              className="rounded-lg bg-white p-4 shadow-sm"
              onClick={(e) => e.preventDefault()}
            >
              <Tabs
                tabs={planTypeTabs}
                selectedTabId={planType}
                // EPS-4856 (B-5) idi: tab değişiminde form tamamen reset ediliyordu.
                // EPS-4970: sekme artık yalnızca fiyatlandırma bölümünü ayırıyor,
                // üstteki ortak alanlar (plan bilgileri, kategori) korunmalı — tam
                // reset kullanıcının girdiklerini siliyordu. Bunun yerine sadece
                // KARŞI tipin alanları temizleniyor: zod tüm şemayı doğruladığı için
                // orada kalan geçersiz bir değer (ör. %150 oran) submit'i sessizce
                // bloklardı.
                onTabChange={(id) => {
                  setPlanType(id);
                  form.clearErrors();
                  if (id === "BAREM") {
                    form.setValue("rateBarems", []);
                  } else {
                    form.setValue("barems", [
                      {
                        minPrice: undefined,
                        maxPrice: undefined,
                        planPrice: undefined,
                        packageCode: "",
                      },
                    ]);
                    // Standart Plana dönüşte fiyat aralığı bölümü boş kalmasın —
                    // aralık artık planın tek fiyat kaynağı.
                    form.setValue("rateBarems", [
                      {
                        minPrice: undefined,
                        maxPrice: undefined,
                        rate: undefined,
                        packageCode: "",
                      },
                    ]);
                  }
                }}
              />
            </div>

            {planType === "BAREM" && (
              <div className="space-y-6">
              {/* EPS-4855: Çoklu fiyat aralığı + paket kodu — her satır kendi
                 min/max/plan/packageCode'una sahip. Kullanıcı + butonuna
                 basarak yeni satır ekler, çöp ikonuyla siler. En az 1 satır
                 zorunlu. */}
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">
                    {t(
                      "extendedWarrantiesPage.baremPriceInfo",
                      "Fiyat Aralıkları",
                    )}
                  </h2>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      baremsArray.append({
                        minPrice: undefined,
                        maxPrice: undefined,
                        planPrice: undefined,
                        packageCode: "",
                      })
                    }
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    {t("extendedWarranties.addBaremRow", "Aralık ekle")}
                  </Button>
                </div>

                <div className="space-y-4">
                  {baremsArray.fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="rounded-md border border-gray-200 p-4"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          {t("extendedWarranties.baremRow", "Aralık {{n}}", {
                            n: index + 1,
                          })}
                        </span>
                        {baremsArray.fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => baremsArray.remove(index)}
                            className="text-red-500 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <FormField
                          control={form.control}
                          name={`barems.${index}.minPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.minPrice",
                                  "Minimum Fiyat",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`barems.${index}.maxPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.maxPrice",
                                  "Maksimum Fiyat",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`barems.${index}.planPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.planPrice",
                                  "Plan Fiyatı",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`barems.${index}.packageCode`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.packageCode",
                                  "Paket Kodu",
                                )}
                              </Label>
                              <FormControl>
                                <Input maxLength={64} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <FormDescription className="mt-3">
                  {t(
                    "extendedWarranties.baremRowsHint",
                    "Her aralık için minimum-maksimum ürün fiyatı, plan fiyatı ve opsiyonel paket kodu girin.",
                  )}
                </FormDescription>
              </div>
              </div>
            )}

            {planType === "BASIC" && (
            <div className="space-y-6">
            {/* EPS-4970: Standart Planın oransal fiyat aralıkları. Baremli plandaki
               aralık yapısının aynısı; tek fark aralığa düz fiyat yerine ORAN
               girilmesi ve paket fiyatının ürünün kendi bedelinden hesaplanması.
               Revizeden sonra planın TEK fiyat kaynağı burası — en az 1 aralık
               zorunlu (Plan Detayları bölümü kaldırıldı). */}
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {t(
                    "extendedWarrantiesPage.rateBaremPriceInfo",
                    "Fiyat Aralıkları (Oransal)",
                  )}
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    rateBaremsArray.append({
                      minPrice: undefined,
                      maxPrice: undefined,
                      rate: undefined,
                      packageCode: "",
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {t("extendedWarranties.addBaremRow", "Aralık ekle")}
                </Button>
              </div>

              {rateBaremsArray.fields.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                  {t(
                    "extendedWarranties.rateBaremsRequiredEmpty",
                    "Aralık tanımlanmadı. Paket fiyatı aralıklardan hesaplandığı için en az bir aralık girmelisiniz.",
                  )}
                </p>
              ) : (
                <div className="space-y-4">
                  {rateBaremsArray.fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="rounded-md border border-gray-200 p-4"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          {t("extendedWarranties.baremRow", "Aralık {{n}}", {
                            n: index + 1,
                          })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => rateBaremsArray.remove(index)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.minPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.minPrice",
                                  "Minimum Fiyat",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.maxPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.maxPrice",
                                  "Maksimum Fiyat",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.rate`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t("extendedWarranties.rate", "Oran")}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">%</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.packageCode`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.packageCode",
                                  "Paket Kodu",
                                )}
                                <span className="ml-0.5 text-red-500">*</span>
                              </Label>
                              <FormControl>
                                <Input maxLength={64} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <FormDescription className="mt-3">
                {t(
                  "extendedWarranties.rateBaremRowsHint",
                  "Her aralık için minimum-maksimum ürün fiyatı, uygulanacak oran ve paket kodunu girin. Paket fiyatı, ürünün kendi bedeli üzerinden hesaplanır (ürün fiyatı × oran). Paket kodu sigorta kanallarına gönderilen isteklerde kullanılır.",
                )}
              </FormDescription>
            </div>
            </div>
            )}
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">
                {t(
                  "extendedWarranties.contractPdf",
                  "Sözleşme PDF",
                )}
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {t(
                  "extendedWarranties.contractPdfDescription",
                  "Bu pakete ait sözleşme PDF dosyasını yükleyebilirsiniz (opsiyonel, maks. 10 MB).",
                )}
              </p>
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handlePdfSelect}
              />
              {contractPdfFile ? (
                <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                  <FileText className="h-5 w-5 shrink-0 text-red-500" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
                    {contractPdfFile.name}
                  </span>
                  <span className="shrink-0 text-xs text-gray-400">
                    {(contractPdfFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                  <button
                    type="button"
                    onClick={() => setContractPdfFile(null)}
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => pdfInputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-600"
                >
                  <Upload className="h-5 w-5" />
                  {t(
                    "extendedWarranties.selectPdf",
                    "PDF dosyası seçin",
                  )}
                </button>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <Button size="lg" type="submit">
                {t("common.save")}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </main>
  );
};

export default NewPackagePage;
